import { useEffect } from "react";
import { createPortal } from "react-dom";
import RecentPanel from "./RecentPanel";

type HistoryDrawerProps = {
  open: boolean;
  onClose: () => void;
  onNewChat: () => void;
  onSelectConversation: (id: string) => void;
};

// Right-hand side drawer listing past conversations, opened from the
// "Chat history" button in the conversation header.
export default function HistoryDrawer({ open, onClose, onNewChat, onSelectConversation }: HistoryDrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Portalled to <body> so the dim covers the whole screen — sidebar and header included.
  return createPortal(
    <>
      <div onClick={onClose} aria-hidden="true" className={`tw-fixed tw-inset-0 tw-z-[2000] tw-bg-gray-900/40 tw-transition-opacity tw-duration-200 ${open ? "tw-opacity-100" : "tw-opacity-0 tw-pointer-events-none"}`} />
      <aside role="dialog" aria-label="Chat history" aria-hidden={!open} className={`tw-fixed tw-top-0 tw-right-0 tw-bottom-0 tw-z-[2001] tw-w-[320px] tw-max-w-full tw-flex tw-flex-col tw-bg-white tw-border-l tw-border-solid tw-border-gray-200 tw-shadow-xl tw-transition-transform tw-duration-200 ${open ? "tw-translate-x-0" : "tw-translate-x-full tw-invisible"}`}>
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-shrink-0 tw-h-12 tw-box-border tw-px-4 tw-shadow-header">
          <span className="tw-text-base tw-font-semibold tw-leading-6 tw-text-gray-900">Chat history</span>
          <button onClick={onClose} aria-label="Close chat history" className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-border-none tw-bg-transparent tw-cursor-pointer tw-text-gray-500 hover:tw-text-gray-900">
            <span className="material-symbols-outlined !tw-text-[20px]">close</span>
          </button>
        </div>
        <div className="tw-flex-grow tw-min-h-0">
          <RecentPanel
            embedded
            onNewChat={onNewChat}
            onSelectConversation={(id) => {
              onSelectConversation(id);
              onClose();
            }}
          />
        </div>
      </aside>
    </>,
    document.body
  );
}
